import { css, html, LitElement, nothing } from "lit";
import { customElement, property, state } from "lit/decorators";
import { SvgIcons } from "../../voyage-svg-icons/src/voyage-svg-icons";
import { ImminentRiskModel } from "../../../src/models/imminent-risk-model";
import { audioAlertStyles } from "../audio-styles";
import audioAlert from "../../../assets/audio/vessel-alert.mp3";
@customElement('test-audio-alert')
export class TestAudioAlert extends LitElement {

    @state() private _currentStep = 0;
    @state() private _isPlaying = false;
    @state() private _userHeardAudio: boolean | null = null;
    @state() private _hasPlayedOnce = false;

    connectedCallback(){
      super.connectedCallback();
      this._stopAudio();
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this._stopAudio();
    }

    private _audio?: HTMLAudioElement;

    static get styles(){
        return [audioAlertStyles];
    }

    private async _finish() {
        if (this._userHeardAudio === null) {
            return;
        }
        this.dispatchEvent(new Event('close'));
    }

    private _playAudio() {
        if (this._isPlaying) {
            this._audio?.pause();
            this._audio!.currentTime = 0;
            this._isPlaying = false;
            return;
        }
        
        this._audio = new Audio(audioAlert);
        this._audio.volume = 70 / 100; // Set volume to 70%
        this._isPlaying = true;

        this._audio.play()
            .then(() => {this._hasPlayedOnce = true; console.log('Audio started')})
            .catch(err => {
            console.error('Error playing audio:', err);
            this._isPlaying = false;
            });

        // When audio finishes naturally, reset UI
        this._audio.addEventListener('ended', () => {
            this._isPlaying = false;
        });
    }

    private _stopAudio() {
      if (this._audio) {
        this._audio.pause();
        this._audio.currentTime = 0;
        this._isPlaying = false;
      }
    }

    private get _steps() {
    return [
      {
        icon: SvgIcons.playButton,
        content: html`
          <h3>Test Alert Audio:</h3>
          <p>Tap “Play” to check if your system is playing audio.</p>
          <mc-button
            appearance="secondary"
            @click=${this._playAudio}
            ?disabled=${this._isPlaying}
          >
            ${this._isPlaying ? '⏸ Playing…' : '▶ Play'}
          </mc-button>
        `,
        footer: html`${this._isPlaying
        ? html`
            <mc-button appearance="neutral" @click=${() => this.showPageAccordingtoUserResponse(false)}>No</mc-button>
            <mc-button appearance="primary" @click=${() => this.showPageAccordingtoUserResponse(true)}>Yes, I can hear it</mc-button>
            `
        : html`
            <mc-button appearance="primary" @click=${async () => {this.showPageAccordingtoUserResponse(true);}}>Yes, I can hear it</mc-button>
            `}
        `
      },
      {
        icon: SvgIcons.speakerOn,
        content: html`
            <div class="troubleshooting">
            <h3>Test Alert Audio | Troubleshooting guideline</h3>
            <p>As it looks like you can't hear the audio, please follow the steps below to troubleshoot.</p>
            <ul>
            <li>✔ Check System Volume & Mute: Ensure your device volume is turned up and not muted.</li>
            <li>✔ Verify Audio Output Device: Ensure the correct device (speakers/headphones) is connected.</li>
            <li>✔ Allow Sound Permissions: When prompted, click "Allow" on your browser's audio permision popup. <br /> If you missed it, follow this: <i>Browser Settings → Site Settings → Sound → Allow</i></li>
            <li>✔ Restart Browser or Device: Sometimes a reboot resolves output issues.</li>
            <li>✔ Disable ‘Do Not Disturb’ Mode: Ensure your system isn’t muting notifications.</li>
            <li>✔ Run the App’s Test Sound:
                <mc-button appearance="secondary" @click=${this._playAudio}>
                ${this._isPlaying ? '⏸ Pause' : '▶ Play'}
                </mc-button>
              </li>
            </ul>
            </div>
        `,
        footer: html`
            <mc-button appearance="primary" @click=${this._finish}>Got it</mc-button>
        `
      },
      {
        icon: SvgIcons.allCheck,
        content: html`
          <h3>You are all set!</h3>
          <p>Your system is ready now to get an audio alert.</p>
        `,
        footer: html`
          <mc-button appearance="primary" @click=${this._finish}>Got it</mc-button>
        `
      }
    ];
  }

  private showPageAccordingtoUserResponse(heard: boolean) {
      this._stopAudio();
      if (heard) {
          this._userHeardAudio = heard; 
          console.log('User CAN hear the audio.');
          this._currentStep = 2; //trouble shooting page
      } else {
          this._userHeardAudio = heard;
          console.log('User could NOT hear the audio.');
          this._currentStep = 1;
      }
  } 

    render(){
        const step = this._steps[this._currentStep];
        const isTroubleshooting = this._currentStep === 1;

        return html`
            <mc-modal
                ?open=${true}
                hiddenclose
                entercloseactiondisabled
                escapecloseactiondisabled
                height="auto"
                padding="none"
            >
            <div class="content">
                <div class="header"><p>Test Audio Alert</p></div>
                <div class="modal-content-test">
                ${!isTroubleshooting
                ? html`
                    <div class="left">
                    <span style="font-size: 48px;">${step.icon}</span>
                    </div>
                `
                : nothing}
                <div class="right">${step.content}</div>
                </div>
                <div class="footer">${step.footer}</div>
            </div>
            </mc-modal>
        `
    }
}